/**
 * TennisDemo — 全屏展示"挥拍击碎数据"物理力场特效
 * 路由: /tennis-demo
 */
import { useEffect, useRef, useCallback, useState } from 'react'

// ─── 配置 ───────────────────────────────────────────────────────────────────
const WORDS = [
  'motion_capture', 'velocity', 'swing_path', 'kinetic_chain',
  'AI_analysis', 'follow_through', 'biomechanics', 'spin_rate',
  'wrist_snap', 'forehand', 'backhand', 'contact_point',
  'topspin', 'flat_drive', 'slice', 'volley',
  'serve_speed', 'footwork', 'court_coverage', 'rally_depth',
  'deep_learning', 'pose_estimation', 'keypoint', 'tensor',
  'accuracy:98.6%', 'fps:120', 'latency:12ms', 'model:YOLOv8',
  'node[0]', 'node[5]', 'node[11]', 'node[16]',
  'Δθ=+2.3°', 'ω=820rpm', 'v=142km/h', 'E=0.94kJ',
  'LAFIT.CLUB', 'AI_COACH', 'SMART_TENNIS', 'DATA_DRIVEN',
]

const CELL_SIZE = 60
const BALL_RADIUS = 16
const BALL_REPEL_DIST = 240
const MOUSE_DIST = 160
const SPRING_K = 0.026
const DAMPING = 0.87
const REPEL_FORCE = 8.5
const MOUSE_FORCE = 1.4
const TRAIL_LEN = 22

interface Particle {
  text: string
  tx: number; ty: number
  x: number; y: number
  vx: number; vy: number
  alpha: number
  hitFlash: number
  fontSize: number
}

interface SpatialHash {
  cells: Map<string, number[]>
  insert(idx: number, x: number, y: number): void
  query(x: number, y: number, radius: number): number[]
  clear(): void
}

function createSpatialHash(): SpatialHash {
  const cells = new Map<string, number[]>()
  const key = (cx: number, cy: number) => `${cx},${cy}`
  return {
    cells,
    insert(idx, x, y) {
      const k = key(Math.floor(x / CELL_SIZE), Math.floor(y / CELL_SIZE))
      if (!cells.has(k)) cells.set(k, [])
      cells.get(k)!.push(idx)
    },
    query(x, y, radius) {
      const result: number[] = []
      const r = Math.ceil(radius / CELL_SIZE)
      const cx0 = Math.floor(x / CELL_SIZE)
      const cy0 = Math.floor(y / CELL_SIZE)
      for (let dx = -r; dx <= r; dx++)
        for (let dy = -r; dy <= r; dy++) {
          const arr = cells.get(key(cx0 + dx, cy0 + dy))
          if (arr) result.push(...arr)
        }
      return result
    },
    clear() { cells.clear() },
  }
}

function computeLayout(
  words: string[],
  W: number, H: number,
  ctx: CanvasRenderingContext2D,
): { text: string; tx: number; ty: number; fontSize: number }[] {
  const result: { text: string; tx: number; ty: number; fontSize: number }[] = []
  const fontSizes = [11, 12, 13, 14, 15]
  const lineHeight = 30
  const paddingX = 40
  const paddingY = 40
  let x = paddingX, y = paddingY + 14, wordIdx = 0
  while (y < H + lineHeight * 2) {
    const text = words[wordIdx % words.length]
    const fs = fontSizes[Math.floor(Math.random() * fontSizes.length)]
    ctx.font = `${fs}px 'Courier New', monospace`
    const w = ctx.measureText(text).width + 24
    if (x + w > W - paddingX) {
      x = paddingX + (Math.random() * 24 - 12)
      y += lineHeight + Math.random() * 10
    }
    result.push({ text, tx: x + w / 2, ty: y, fontSize: fs })
    x += w
    wordIdx++
    if (wordIdx > words.length * 30) break
  }
  return result
}

interface BallState {
  progress: number
  x: number; y: number
  angle: number
  trail: { x: number; y: number }[]
}

function updateBall(ball: BallState, W: number, H: number): void {
  const t = ball.progress
  ball.x = -BALL_RADIUS + (W + BALL_RADIUS * 2) * t
  ball.y = H * 0.38 + (H * 0.55 - H * 0.38) * t + (-H * 0.14) * 4 * t * (1 - t)
  ball.angle += 6
  ball.trail.push({ x: ball.x, y: ball.y })
  if (ball.trail.length > TRAIL_LEN) ball.trail.shift()
}

// ─── 主页面 ───────────────────────────────────────────────────────────────────
export default function TennisDemo() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isRunning, setIsRunning] = useState(true)
  const stateRef = useRef<{
    particles: Particle[]
    spatialHash: SpatialHash
    ball: BallState
    mouseX: number; mouseY: number
    animId: number
    tick: number
    phase: 'wait' | 'flying' | 'idle'
    phaseTimer: number
    paused: boolean
  } | null>(null)

  const init = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d')!
    const W = canvas.width, H = canvas.height
    const layouts = computeLayout(WORDS, W, H, ctx)
    const particles: Particle[] = layouts.map(({ text, tx, ty, fontSize }) => ({
      text, tx, ty,
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      alpha: 0.15 + Math.random() * 0.30,
      hitFlash: 0,
      fontSize,
    }))

    const ball: BallState = { progress: 0, x: -BALL_RADIUS, y: H * 0.38, angle: 0, trail: [] }

    stateRef.current = {
      particles,
      spatialHash: createSpatialHash(),
      ball,
      mouseX: W / 2, mouseY: H / 2,
      animId: 0, tick: 0,
      phase: 'wait', phaseTimer: 0,
      paused: false,
    }
  }, [])

  const render = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !stateRef.current) return
    const s = stateRef.current
    if (s.paused) { s.animId = requestAnimationFrame(render); return }

    const ctx = canvas.getContext('2d')!
    const W = canvas.width, H = canvas.height
    const { particles, spatialHash, ball } = s

    s.tick++
    s.phaseTimer++

    // 阶段控制
    if (s.phase === 'wait' && s.phaseTimer > 80) {
      s.phase = 'flying'; s.phaseTimer = 0
      ball.progress = 0; ball.trail = []
    } else if (s.phase === 'flying') {
      ball.progress += 0.007
      if (ball.progress >= 1.0) { s.phase = 'idle'; s.phaseTimer = 0 }
    } else if (s.phase === 'idle' && s.phaseTimer > 280) {
      s.phase = 'wait'; s.phaseTimer = 0
      ball.progress = 0; ball.trail = []
    }

    if (s.phase === 'flying') updateBall(ball, W, H)

    // 空间哈希重建
    spatialHash.clear()
    particles.forEach((p, i) => spatialHash.insert(i, p.x, p.y))

    const ballActive = s.phase === 'flying'
    const nearBall = ballActive
      ? new Set(spatialHash.query(ball.x, ball.y, BALL_REPEL_DIST))
      : new Set<number>()
    const nearMouse = new Set(spatialHash.query(s.mouseX, s.mouseY, MOUSE_DIST))

    // 物理更新
    particles.forEach((p, i) => {
      // 弹簧回归
      p.vx += (p.tx - p.x) * SPRING_K
      p.vy += (p.ty - p.y) * SPRING_K

      // 网球排斥
      if (nearBall.has(i) && ballActive) {
        const bx = p.x - ball.x, by = p.y - ball.y
        const dist = Math.sqrt(bx * bx + by * by)
        if (dist < BALL_REPEL_DIST && dist > 0.1) {
          const falloff = 1 - dist / BALL_REPEL_DIST
          const f = REPEL_FORCE * falloff * falloff
          p.vx += (bx / dist) * f
          p.vy += (by / dist) * f
          if (dist < BALL_RADIUS * 4) p.hitFlash = Math.min(1, p.hitFlash + 0.55)
        }
      }

      // 鼠标气流
      if (nearMouse.has(i)) {
        const mx = p.x - s.mouseX, my = p.y - s.mouseY
        const dist = Math.sqrt(mx * mx + my * my)
        if (dist < MOUSE_DIST && dist > 0.1) {
          const f = MOUSE_FORCE * (1 - dist / MOUSE_DIST)
          p.vx += (mx / dist) * f * 0.45
          p.vy += (my / dist) * f * 0.45
        }
      }

      p.vx *= DAMPING; p.vy *= DAMPING
      p.x += p.vx; p.y += p.vy
      if (p.x < 0) { p.x = 0; p.vx *= -0.3 }
      if (p.x > W) { p.x = W; p.vx *= -0.3 }
      if (p.y < 0) { p.y = 0; p.vy *= -0.3 }
      if (p.y > H) { p.y = H; p.vy *= -0.3 }
      p.hitFlash *= 0.91
    })

    // ── 绘制 ──────────────────────────────────────────────────────────────
    // 深色背景
    ctx.fillStyle = '#06060f'
    ctx.fillRect(0, 0, W, H)

    // 细微网格
    ctx.save()
    ctx.strokeStyle = 'rgba(255,255,255,0.025)'
    ctx.lineWidth = 0.5
    const gridSize = 60
    for (let gx = 0; gx < W; gx += gridSize) {
      ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke()
    }
    for (let gy = 0; gy < H; gy += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke()
    }
    ctx.restore()

    // 文字粒子
    particles.forEach(p => {
      const fl = p.hitFlash
      ctx.save()
      ctx.globalAlpha = p.alpha + fl * (1 - p.alpha)
      ctx.font = `${p.fontSize}px 'Courier New', monospace`
      ctx.textBaseline = 'middle'
      if (fl > 0.05) {
        ctx.shadowColor = `rgba(204,255,0,${fl * 0.9})`
        ctx.shadowBlur = 10 * fl
        const r = Math.floor(74 + (255 - 74) * fl)
        const g = Math.floor(85 + (255 - 85) * fl)
        const b = Math.floor(104 + (255 - 104) * fl)
        ctx.fillStyle = `rgb(${r},${g},${b})`
      } else {
        ctx.fillStyle = '#4a5568'
      }
      ctx.fillText(p.text, p.x - ctx.measureText(p.text).width / 2, p.y)
      ctx.restore()
    })

    // 轨迹残影
    if (ball.trail.length > 1) {
      ctx.save()
      for (let i = 1; i < ball.trail.length; i++) {
        const t = i / ball.trail.length
        const prev = ball.trail[i - 1], curr = ball.trail[i]
        ctx.beginPath()
        ctx.moveTo(prev.x, prev.y)
        ctx.lineTo(curr.x, curr.y)
        ctx.strokeStyle = `rgba(204,255,0,${t * 0.6})`
        ctx.lineWidth = t * BALL_RADIUS * 1.0
        ctx.lineCap = 'round'
        ctx.stroke()
      }
      ctx.restore()
    }

    // 网球
    if (s.phase === 'flying' || (s.phase === 'idle' && s.phaseTimer < 40)) {
      ctx.save()
      ctx.translate(ball.x, ball.y)
      ctx.rotate((ball.angle * Math.PI) / 180)
      ctx.shadowColor = 'rgba(204,255,0,0.7)'
      ctx.shadowBlur = 24
      const grad = ctx.createRadialGradient(-5, -5, 2, 0, 0, BALL_RADIUS)
      grad.addColorStop(0, '#eeff77')
      grad.addColorStop(0.5, '#ccff00')
      grad.addColorStop(1, '#88bb00')
      ctx.beginPath()
      ctx.arc(0, 0, BALL_RADIUS, 0, Math.PI * 2)
      ctx.fillStyle = grad
      ctx.fill()
      ctx.strokeStyle = 'rgba(255,255,255,0.55)'
      ctx.lineWidth = 1.3
      ctx.shadowBlur = 0
      ctx.beginPath(); ctx.arc(0, 0, BALL_RADIUS * 0.62, -0.4 * Math.PI, 0.4 * Math.PI); ctx.stroke()
      ctx.beginPath(); ctx.arc(0, 0, BALL_RADIUS * 0.62, 0.6 * Math.PI, 1.4 * Math.PI); ctx.stroke()
      ctx.restore()
    }


    s.animId = requestAnimationFrame(render)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      init(canvas)
    }
    resize()
    window.addEventListener('resize', resize)
    const onMouseMove = (e: MouseEvent) => {
      if (stateRef.current) {
        stateRef.current.mouseX = e.clientX
        stateRef.current.mouseY = e.clientY
      }
    }
    window.addEventListener('mousemove', onMouseMove)
    if (stateRef.current) {
      stateRef.current.animId = requestAnimationFrame(render)
    }
    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMouseMove)
      if (stateRef.current) cancelAnimationFrame(stateRef.current.animId)
    }
  }, [init, render])

  const togglePause = () => {
    if (!stateRef.current) return
    stateRef.current.paused = !stateRef.current.paused
    setIsRunning(!stateRef.current.paused)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#06060f', overflow: 'hidden' }}>
      {/* 全屏 Canvas */}
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, display: 'block' }}
      />

      {/* 底部主文字（不遮挡力场效果区域） */}
      <div style={{
        position: 'absolute', bottom: '72px', left: 0, right: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '24px 32px 20px',
          background: 'linear-gradient(to top, rgba(6,6,15,0.92) 60%, transparent)',
          borderRadius: '16px',
          backdropFilter: 'blur(0px)',
        }}>
          <div style={{
            fontSize: '11px', letterSpacing: '4px', textTransform: 'uppercase',
            color: '#ccff00', marginBottom: '10px', opacity: 0.7,
            fontFamily: '"Courier New", monospace',
          }}>
            Lafit Technology · AI-Coach
          </div>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 64px)',
            fontWeight: 900, color: '#fff',
            margin: '0 0 8px',
            lineHeight: 1.1,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            运动科技&nbsp;
            <span style={{
              background: 'linear-gradient(135deg, #ccff00 0%, #00ff88 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              智享未来
            </span>
          </h1>
          <p style={{
            fontSize: 'clamp(12px, 1.4vw, 16px)',
            color: 'rgba(255,255,255,0.45)',
            margin: 0,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            AI 穿透数据 · 捕捉每一个击球瞬间
          </p>
        </div>
      </div>

      {/* 顶部导航栏 */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 32px',
        background: 'linear-gradient(to bottom, rgba(6,6,15,0.8), transparent)',
        pointerEvents: 'none',
      }}>
        <div style={{
          fontSize: '16px', fontWeight: 700,
          color: '#fff', letterSpacing: '1px',
          fontFamily: '-apple-system, sans-serif',
        }}>
          <span style={{ color: '#ccff00' }}>LAFIT</span> · AI Coach
        </div>
        <div style={{
          fontSize: '11px', color: 'rgba(255,255,255,0.35)',
          fontFamily: '"Courier New", monospace',
          letterSpacing: '2px',
        }}>
          PHYSICS ENGINE DEMO
        </div>
      </div>

      {/* 底部控制栏 */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 32px',
        background: 'linear-gradient(to top, rgba(6,6,15,0.85), transparent)',
      }}>
        <div style={{
          fontSize: '11px', color: 'rgba(255,255,255,0.3)',
          fontFamily: '"Courier New", monospace',
        }}>
          移动鼠标产生气流扰动 · 网球每 ~6s 循环飞行
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={togglePause}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: isRunning ? '#ccff00' : '#94a3b8',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              cursor: 'pointer',
              letterSpacing: '1px',
              fontFamily: '"Courier New", monospace',
              transition: 'all 0.2s',
            }}
          >
            {isRunning ? '⏸ PAUSE' : '▶ PLAY'}
          </button>
          <a
            href="/#/aicoach"
            style={{
              background: 'linear-gradient(135deg, #ccff00, #00ff88)',
              color: '#06060f',
              padding: '6px 18px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 700,
              textDecoration: 'none',
              letterSpacing: '1px',
              fontFamily: '-apple-system, sans-serif',
            }}
          >
            查看 AI Coach →
          </a>
        </div>
      </div>
    </div>
  )
}
